import { useContext } from 'react';
import debounce from 'lodash.debounce';

import { Checkbox, Icon, IconButton } from '@atom/mui';
import colors from '@atom/styles/colors';

import BudgetDetailContext from '../BudgetDetailContext';
import { DEBOUNCE_CHECKBOX } from '../budgetDetailUtils';
import { FILTER_KEYS, useBudgetFilterStorage } from '../useBudgetFilterStorage';

import BudgetDetailCategoryFilter from './BudgetDetailCategoryFilter';
import BudgetDetailComparisonFilter from './BudgetDetailComparisonFilter';
import BudgetDetailItemFilter from './BudgetDetailItemFilter';
import BudgetDetailTypeFilter from './BudgetDetailTypeFilter';

const styles = {
  fixedFiltersContainer: {
    position: 'fixed',
    height: '75px',
    marginTop: '110px',
    padding: '1rem 1.5rem',
    top: 0,
    left: 0,
    right: 0,
    backgroundColor: colors.neutral.white,
    zIndex: 100,
  },

  filterContainer: {
    padding: '0.5rem 0',
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
  },

  filterLabel: {
    color: colors.neutral.gray,
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
  },

  separator: {
    height: '2rem',
    width: '1px',
    backgroundColor: colors.neutral.ash,
    display: 'inline-block',
    padding: 0,
    marginLeft: '0.25rem',
  },
  fixedInfoIconButton: {
    padding: '0',
    height: '1.5rem',
  },
  chartInfoButton: {
    padding: '0',
    height: '1.5rem',
    marginLeft: '-1rem',
  },
  tooltipCopy: {
    padding: '1rem',
  },
  tooltipList: {
    margin: 0,
    padding: 0,
  },
  fixedInfoIcon: { fontSize: '0.75rem' },
};

const tooltipCopy = (
  <div style={styles.tooltipCopy}>
    <ul style={styles.tooltipList}>
      <li>
        <strong>Solid Line (Past Months)</strong>: Tracks the total remaining
        using actual costs.
      </li>
      <li>
        <strong>Dashed Line (Current Month)</strong>: Projects the total
        remaining using actual costs plus future fixed costs for the rest of the
        month.
      </li>
      <li>
        <strong>Dashed Line (Future Months)</strong>: Projects the total
        remaining based only on future fixed costs (even monthly distribution).
      </li>
    </ul>
  </div>
);

const BudgetDetailFilters = () => {
  const {
    showTracking,
    setShowTracking,
    showExpenditures,
    showChart,
    setShowChart,
    showBudgetTypes,
  } = useContext(BudgetDetailContext);

  const { saveFilterState } = useBudgetFilterStorage();

  const handleTrackingToggle = debounce(() => {
    setShowTracking(prev => {
      saveFilterState(FILTER_KEYS.TRACKING, !prev);
      return !prev;
    });
  }, DEBOUNCE_CHECKBOX);

  const handleChartToggle = debounce(() => {
    setShowChart(prev => {
      saveFilterState(FILTER_KEYS.CHART, !prev);
      return !prev;
    });
  }, DEBOUNCE_CHECKBOX);

  return (
    <div style={styles.filterContainer}>
      <div style={styles.filterLabel}>Filters:</div>
      {showBudgetTypes && <BudgetDetailTypeFilter />}
      <BudgetDetailCategoryFilter />
      <BudgetDetailItemFilter />
      <div style={styles.separator} />
      <div style={styles.filterLabel}>
        <span>Compare</span>
        <IconButton
          tooltip="Maximum 5 comparison budget selections"
          style={styles.fixedInfoIconButton}
        >
          <Icon style={styles.fixedInfoIcon}>info_outlined</Icon>
        </IconButton>
      </div>
      <BudgetDetailComparisonFilter />
      {showExpenditures && (
        <>
          <div style={styles.separator} />
          <Checkbox
            onChange={handleTrackingToggle}
            checked={showTracking}
            label="Show Tracking"
          />
        </>
      )}
      {showExpenditures && showTracking && (
        <>
          <Checkbox
            onChange={handleChartToggle}
            checked={showChart}
            label="Show Chart"
          />
          <IconButton style={styles.chartInfoButton} tooltip={tooltipCopy}>
            <Icon style={styles.fixedInfoIcon}>info_outlined</Icon>
          </IconButton>
        </>
      )}
    </div>
  );
};

export default BudgetDetailFilters;
